import React, { useState } from 'react';
import { Share2, Copy, Check, X, MessageCircle, Twitter } from 'lucide-react';
import { Button } from './ui/button';

const ShareButton = ({ 
  title, 
  description, 
  url, 
  image,
  shareType = null, // 'post' or 'issue' for dynamic OG tags
  itemId = null,    // post_id or issue_id
  variant = 'default', // 'default', 'icon', 'small'
  className = ''
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;
  const shareText = description || '';
  
  // Generate social share URL - ALWAYS use /api/share/ for proper OG tags
  const getSocialShareUrl = () => {
    if (shareType && itemId) {
      // Use current origin for share URL - works for both preview and production
      const origin = window.location.origin;
      return `${origin}/api/share/${shareType}/${itemId}`;
    }
    return shareUrl;
  };

  const handleShare = async () => {
    // Get the correct social share URL
    const socialUrl = getSocialShareUrl();
    
    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: socialUrl  // Use social share URL
        });
        return;
      } catch (err) {
        // User cancelled or error, fall through to popup
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    }
    
    // Fallback: show popup
    setShowPopup(true);
  };

  const handleCopyLink = async () => {
    // Copy the social share URL for proper OG tags
    const socialUrl = getSocialShareUrl();
    try {
      await navigator.clipboard.writeText(socialUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = socialUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToWhatsApp = () => {
    // Use social share URL for proper OG tags
    const socialUrl = getSocialShareUrl();
    const text = encodeURIComponent(`${shareTitle}\n${shareText}\n${socialUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowPopup(false);
  };

  const shareToTwitter = () => {
    // Use social share URL for proper OG tags
    const socialUrl = getSocialShareUrl();
    const text = encodeURIComponent(`${shareTitle} ${shareText}`);
    const urlParam = encodeURIComponent(socialUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${urlParam}`, '_blank');
    setShowPopup(false);
  };

  const shareToFacebook = () => {
    // Use social share URL for proper OG tags - MUST be /api/share/post/{id}
    const socialUrl = getSocialShareUrl();
    const urlParam = encodeURIComponent(socialUrl);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlParam}`;
    window.open(facebookShareUrl, '_blank');
    setShowPopup(false);
  };

  // Render based on variant
  const renderButton = () => {
    if (variant === 'icon') {
      return (
        <button
          onClick={handleShare}
          className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${className}`}
          title="Share"
        >
          <Share2 className="h-4 w-4 text-gray-600" />
        </button>
      );
    }
    
    if (variant === 'small') {
      return (
        <button
          onClick={handleShare}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors text-sm ${className}`}
        >
          <Share2 className="h-4 w-4" />
          Share
        </button>
      );
    }

    return (
      <Button
        onClick={handleShare}
        variant="outline"
        size="sm"
        className={className}
      >
        <Share2 className="h-4 w-4 mr-2" />
        Share
      </Button>
    );
  };

  return (
    <>
      {renderButton()}
      
      {/* Share Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Share</h3>
              <button 
                onClick={() => setShowPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Preview */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              {image && (
                <img 
                  src={image} 
                  alt="" 
                  className="w-full h-24 object-cover rounded mb-2"
                />
              )}
              <p className="font-medium text-gray-900 text-sm line-clamp-2">{shareTitle}</p>
              {shareText && (
                <p className="text-xs text-gray-500 line-clamp-2 mt-1">{shareText}</p>
              )}
            </div>
            
            {/* Copy Link */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={getSocialShareUrl()}
                readOnly
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 truncate"
              />
              <button
                onClick={handleCopyLink}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  copied 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            
            {/* Share Options */}
            <div className="flex justify-center gap-4">
              <button
                onClick={shareToWhatsApp}
                className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-green-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </button>
              
              <button
                onClick={shareToTwitter}
                className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                  <Twitter className="h-5 w-5 text-white" />
                </div>
                <span className="text-xs text-gray-600">X (Twitter)</span>
              </button>
              
              <button
                onClick={shareToFacebook}
                className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-600">Facebook</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;
